// Lazy load highcharts package. Copied from Spencer's UIK Highcharts lazy loader.
// The only place in the repo where "highcharts" should be imported.

import { utils } from '@appd/uik';
import { useEffect, useReducer } from 'react';

export type HIGHCHARTS = {
  (): typeof import('highcharts') | undefined;

  use: () => typeof import('highcharts') | undefined;
};

export const HIGHCHARTS: HIGHCHARTS = () => RESOLVED() ?? void LOAD.then(RESOLVED.set);

HIGHCHARTS.use = () => {
  const [, rerender] = useReducer(() => true, false);

  useEffect(() => RESOLVED.subscribe(rerender), []);

  return HIGHCHARTS();
};

const LOAD = utils.CAN_USE_DOM
  ? utils.lazyPromise(async () => {
      const [
        Highcharts,
        HighchartsColorAxis,
        ChartModuleMore,
        HCSolidGauge,
        networkgraph,
        HighchartsHeatmap,
        HighchartsTreeGraph,
        HighchartsTreeMap,
        HighchartsParallelCoordinate,
        HighchartsSunburst,
      ] = await Promise.all([
        import('highcharts'),
        import('highcharts/modules/coloraxis'),
        import('highcharts/highcharts-more.js'),
        import('highcharts/modules/solid-gauge'),
        import('highcharts/modules/networkgraph'),
        import('highcharts/modules/heatmap'),
        import('highcharts/modules/treegraph'),
        import('highcharts/modules/treemap'),
        import('highcharts/modules/parallel-coordinates'),
        import('highcharts/modules/sunburst'),
      ]);

      networkgraph.default(Highcharts);
      HighchartsHeatmap.default(Highcharts);
      ChartModuleMore.default(Highcharts);
      HCSolidGauge.default(Highcharts);
      HighchartsTreeMap.default(Highcharts);
      HighchartsTreeGraph.default(Highcharts);
      HighchartsParallelCoordinate.default(Highcharts);
      HighchartsSunburst.default(Highcharts);

      /*
       * This is used in cases where functions are not available,
       * such as in format as opposed to formatter.
       * See https://www.highcharts.com/docs/chart-concepts/templating
       */
      (Highcharts as any).Templating.helpers.isNull = (value: number) => value == null;

      Highcharts.setOptions({
        lang: {
          thousandsSep: ',',
        },
      });

      return Highcharts;
    })
  : // Don't fetch on server due to errors like
    // TypeError: Cannot read properties of undefined (reading 'Core/Chart/Chart.js')
    new Promise<never>(() => {});

const RESOLVED = utils.mutable<ReturnType<HIGHCHARTS>>(undefined);
